import { Autocomplete, TextField, Typography } from '@mui/material'
import { useMemo, useState, type Ref } from 'react'
import { useMenuItems } from '@/api/hooks'
import type { MenuItem } from '@/api/types'

/**
 * Choosing one thing off the catalogue, for a line charged on top of a sitting.
 *
 * <p>Typed free-hand, the same live counter was "Chaat counter", "Live chaat"
 * and "chat counter" on three quotations, and none of them tied back to what it
 * costs us. Picking it off the master ties the line to the item, brings its
 * rate with it, and lets the kitchen recognise what it has been asked for.</p>
 *
 * <p>Searched on the server by code or name, so a catalogue of thousands is not
 * pulled into the browser to be filtered there.</p>
 */
export function ExtraItemPicker({
  value,
  label,
  onChange,
  inputRef,
}: {
  /** The dish already chosen, if any. */
  value: MenuItem | null
  label: string
  onChange: (item: MenuItem | null) => void
  /** Handed out so a form can put the cursor back here after a line is added. */
  inputRef?: Ref<HTMLInputElement>
}) {
  const [term, setTerm] = useState('')
  // The whole catalogue, not only what a package may be built from. A line
  // charged on top of a sitting is not a menu choice: a projector, a generator,
  // a vessel that did not come back are all things the office bills for, and
  // the package builder's narrowing was keeping them out of the one place they
  // are actually sold from.
  const { data, isFetching } = useMenuItems({
    q: term.trim() || undefined,
    size: 50,
  })

  const options = useMemo(() => {
    const byId = new Map<string, MenuItem>()
    if (value) byId.set(value.id, value)
    ;(data?.content ?? []).forEach((item) => byId.set(item.id, item))
    return [...byId.values()].sort((left, right) => left.name.localeCompare(right.name))
  }, [data, value])

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={options}
      value={value}
      loading={isFetching}
      // The server has already matched the term against code and name; matching
      // again here would only narrow what it deliberately returned.
      filterOptions={(all) => all}
      // Opened as soon as it is reached, because a line is added and the cursor
      // comes straight back here for the next one.
      openOnFocus
      onInputChange={(_event, next, reason) => {
        if (reason === 'input') {
          setTerm(next)
          return
        }
        // An empty box searches for nothing in particular, and that has to be
        // true of what is behind it as well.
        //
        // The term used to be left alone whenever the box was not typed in,
        // which is right while a dish is being chosen - selecting one puts its
        // name in the box, and searching for that name again would be a
        // pointless round trip. But the box is also emptied when the choice is
        // taken away, which is what happens the moment a line is added. The
        // search behind it stayed on the last dish, so the next line was
        // offered a catalogue of exactly one item: the one just added. It read
        // as a picker that had stopped working, and the only way past it was to
        // know that typing something fresh would wake it up.
        if (reason === 'clear' || !next) {
          setTerm('')
        }
      }}
      isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
      getOptionLabel={(option) => option.name}
      onChange={(_event, chosen) => onChange(chosen)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Typography variant="body2">
            {option.name}
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              · {option.itemCode}
            </Typography>
          </Typography>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder="Search the catalogue" inputRef={inputRef} inputProps={{
          ...params.inputProps,
          'aria-label': label,
        }} />
      )}
    />
  )
}
